import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { SidebarNavMenuItem } from '../../decorators/sidebar-nav-menu-item';
import { UserEntityService } from '../../services/settings/user-entity.service';
import { UserPrefsService } from '../../services/user-prefs.service';
import { Log } from '../../services/logger.service';

export const IANA_TIMEZONES: { label: string; value: string }[] = [
  { label: 'UTC ±00:00', value: 'UTC' },
  { label: 'London (GMT/BST) ±00:00/+01:00', value: 'Europe/London' },
  { label: 'Central European Time +01:00', value: 'Europe/Paris' },
  { label: 'Eastern European Time +02:00', value: 'Europe/Helsinki' },
  { label: 'Moscow Time +03:00', value: 'Europe/Moscow' },
  { label: 'Gulf Standard Time +04:00', value: 'Asia/Dubai' },
  { label: 'India Standard Time +05:30', value: 'Asia/Kolkata' },
  { label: 'Bangladesh Standard Time +06:00', value: 'Asia/Dhaka' },
  { label: 'Indochina Time +07:00', value: 'Asia/Bangkok' },
  { label: 'China Standard Time +08:00', value: 'Asia/Hong_Kong' },
  { label: 'Singapore Standard Time +08:00', value: 'Asia/Singapore' },
  { label: 'Japan Standard Time +09:00', value: 'Asia/Tokyo' },
  { label: 'Australia Eastern Time +10:00/+11:00', value: 'Australia/Sydney' },
  { label: 'New Zealand Time +12:00/+13:00', value: 'Pacific/Auckland' },
  { label: 'Eastern Time −5:00/−4:00', value: 'America/New_York' },
  { label: 'Central Time −6:00/−5:00', value: 'America/Chicago' },
  { label: 'Mountain Time −7:00/−6:00', value: 'America/Denver' },
  { label: 'Pacific Time −8:00/−7:00', value: 'America/Los_Angeles' },
  { label: 'Alaska Time −9:00/−8:00', value: 'America/Anchorage' },
  { label: 'Hawaii Time −10:00', value: 'Pacific/Honolulu' },
  { label: 'São Paulo (BRT) −3:00', value: 'America/Sao_Paulo' },
  { label: 'Argentina (ART) −3:00', value: 'America/Argentina/Buenos_Aires' },
  { label: 'West Africa Time +01:00', value: 'Africa/Lagos' },
  { label: 'East Africa Time +03:00', value: 'Africa/Nairobi' },
  { label: 'South Africa (SAST) +02:00', value: 'Africa/Johannesburg' },
];

@SidebarNavMenuItem({
  route: '/profile',
  faIcon: 'person_outline',
  name: 'Profile',
  order: 9000
})
@Component({
  selector: 'app-profile',
  templateUrl: './profile.component.html',
  styleUrl: './profile.component.css'
})
export class ProfileComponent implements OnInit {
  private readonly logger = Log.create(ProfileComponent);

  userProfile: any = null;
  loading = true;
  error: string | null = null;

  // ── Timezone ──────────────────────────────────────────────────────────
  readonly timezones = IANA_TIMEZONES;
  get browserTimezone(): string { return Intl.DateTimeFormat().resolvedOptions().timeZone; }
  get displayTimezone(): string {
    return this.userPrefs.get<string>('profile', 'timezone', 'override') ?? this.browserTimezone;
  }
  isEditingTimezone = false;
  pendingTimezone = '';

  startEditTimezone(): void {
    this.pendingTimezone = this.displayTimezone;
    this.isEditingTimezone = true;
  }

  cancelEditTimezone(): void {
    this.isEditingTimezone = false;
  }

  saveTimezone(): void {
    if (this.pendingTimezone) {
      this.userPrefs.set('profile', 'timezone', 'override', this.pendingTimezone);
    }
    this.isEditingTimezone = false;
  }

  resetTimezone(): void {
    this.userPrefs.remove('profile', 'timezone', 'override');
    this.isEditingTimezone = false;
    this.snackBar.open('Timezone reset to browser locale', undefined, { duration: 2500 });
  }

  get isTimezoneOverridden(): boolean {
    return this.userPrefs.get<string>('profile', 'timezone', 'override') !== null;
  }

  // ── Password modal ────────────────────────────────────────────────────
  showPasswordModal = false;
  showNewPassword = false;
  showConfirmPassword = false;
  passwordData = { newPassword: '', confirmPassword: '' };
  passwordValidation = {
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false,
    passwordsMatch: false
  };
  passwordError: string | null = null;
  passwordSuccess: string | null = null;
  isUpdatingPassword = false;

  // ── Settings ──────────────────────────────────────────────────────────
  isResettingPrefs = false;

  constructor(
    private readonly userEntityService: UserEntityService,
    private readonly userPrefs: UserPrefsService,
    private readonly snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    this.loadUserProfile();
  }

  loadUserProfile(): void {
    this.loading = true;
    this.error = null;
    this.userEntityService.userGetByUserId().subscribe({
      next: (response: any) => {
        this.userProfile = response;
        this.loading = false;
      },
      error: (error: any) => {
        this.logger.e('Error loading user profile:', error);
        this.error = 'Failed to load user profile';
        this.loading = false;
      }
    });
  }

  getFirstName(): string {
    if (!this.userProfile?.name) return 'N/A';
    const parts = this.userProfile.name.split(' ');
    return parts[0] || 'N/A';
  }

  getLastName(): string {
    if (!this.userProfile?.name) return 'N/A';
    const parts = this.userProfile.name.split(' ');
    return parts.length > 1 ? parts.slice(1).join(' ') : 'N/A';
  }

  formatRoleName(role: string): string {
    if (!role) return '';
    return role.charAt(0).toUpperCase() + role.slice(1).toLowerCase();
  }

  get isAdhocUser(): boolean {
    return !!(this.userProfile?.details?.CreatedAdHoc ?? this.userProfile?.Details?.CreatedAdHoc);
  }

  // ── Password ──────────────────────────────────────────────────────────
  editPassword(): void { this.showPasswordModal = true; this.resetPasswordForm(); }
  closePasswordModal(): void { this.showPasswordModal = false; this.resetPasswordForm(); }

  resetPasswordForm(): void {
    this.showNewPassword = false;
    this.showConfirmPassword = false;
    this.passwordData = { newPassword: '', confirmPassword: '' };
    this.passwordValidation = { minLength: false, hasUppercase: false, hasLowercase: false, hasNumber: false, hasSpecial: false, passwordsMatch: false };
    this.passwordError = null;
    this.passwordSuccess = null;
  }

  validatePassword(): void {
    const p = this.passwordData.newPassword;
    this.passwordValidation.minLength    = p.length >= 8;
    this.passwordValidation.hasUppercase = /[A-Z]/.test(p);
    this.passwordValidation.hasLowercase = /[a-z]/.test(p);
    this.passwordValidation.hasNumber    = /\d/.test(p);
    this.passwordValidation.hasSpecial   = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(p);
    this.passwordValidation.passwordsMatch = p === this.passwordData.confirmPassword && p.length > 0;
  }

  isPasswordValid(): boolean {
    return Object.values(this.passwordValidation).every(v => v);
  }

  updatePassword(): void {
    if (!this.isPasswordValid()) {
      this.passwordError = 'Please ensure all password requirements are met.';
      return;
    }
    this.isUpdatingPassword = true;
    this.passwordError = null;
    this.passwordSuccess = null;
    this.userEntityService.updatePassword(this.passwordData).subscribe({
      next: () => {
        this.isUpdatingPassword = false;
        this.passwordSuccess = 'Password updated successfully!';
        this.snackBar.open('Password updated successfully!', undefined, { duration: 3000 });
        setTimeout(() => this.closePasswordModal(), 2000);
      },
      error: () => {
        this.isUpdatingPassword = false;
        this.snackBar.open('Password update failed', 'Dismiss', { duration: 4000, panelClass: 'snack-error' });
      }
    });
  }

  // ── Settings ──────────────────────────────────────────────────────────
  resetPreferences(): void {
    this.isResettingPrefs = true;
    this.userPrefs.reset();
    this.isResettingPrefs = false;
    this.snackBar.open('User preferences reset successfully', undefined, { duration: 3000 });
  }
}
